import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { X, Loader2, Banknote } from "lucide-react";
import axios from "axios";
import { GET_DEBT_HISTORY_URL } from "../../../features/UrlHelper";
import { useOrdersRealtime } from "../../../context/RealtimeContext";
import "./DebtHistoryModal.scss";

function formatDateSK(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("sk-SK", { timeZone: "Europe/Bratislava" });
}

function formatDateMobile(dateStr) {
  if (!dateStr) return { dayMonth: "—", year: "" };
  const d = new Date(dateStr);
  if (isNaN(d)) return { dayMonth: "—", year: "" };
  const dayMonth = d.toLocaleDateString("sk-SK", { timeZone: "Europe/Bratislava", day: "numeric", month: "numeric" });
  const year = d.getFullYear();
  return { dayMonth, year };
}

function formatAmount(val) {
  return `${Number(val).toFixed(2).replace(".", ",")} €`;
}

const STATUS_LABELS = {
  ACCEPTED: "Prijatá",
  DELIVERY: "V doručení",
  DELIVERED: "Doručená",
  CANCELLED: "Zrušená",
};

function DebtHistoryModal({ customer, onClose, onOpenPayment }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingItemsFor, setViewingItemsFor] = useState(null);

  const seqRef = useRef(0);

  const loadHistory = useCallback(({ silent = false } = {}) => {
    if (!customer?.id) return;
    const seq = ++seqRef.current;
    if (!silent) setLoading(true);
    axios
      .get(GET_DEBT_HISTORY_URL(customer.id))
      .then(({ data }) => {
        if (seq === seqRef.current) setOrders(data);
      })
      .catch((err) => console.error("Chyba pri načítaní histórie dlhu:", err))
      .finally(() => {
        if (!silent && seq === seqRef.current) setLoading(false);
      });
  }, [customer?.id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Históriu ovplyvnia len udalosti tohto zákazníka (plus dobiehací resync).
  useOrdersRealtime({
    filter: (evt) =>
      evt.entity === "resync" ||
      (evt.customerId != null && Number(evt.customerId) === Number(customer?.id)),
    onChange: () => loadHistory({ silent: true }),
    debounceMs: 800,
  });

  // Sort chronologically, filter to only rows where balance changed, compute running total
  const ordersWithRunning = useMemo(() => {
    const sorted = [...orders].sort(
      (a, b) => new Date(a.delivery_date) - new Date(b.delivery_date)
    );

    const debtChanges = sorted.filter((order, index, array) => {
      const currentBalance = Number(order.balance);
      if (isNaN(currentBalance) || currentBalance === 0) return false;
      if (index === 0) return true;
      const previousOrder = array[index - 1];
      return currentBalance !== Number(previousOrder.balance);
    });

    let running = 0;
    return debtChanges.map((o) => {
      running += parseFloat(o.balance) || 0;
      return { ...o, running_balance: running };
    });
  }, [orders]);

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleRowClick = (order) => {
    if (window.innerWidth < 768 && Array.isArray(order.items) && order.items.length > 0) {
      setViewingItemsFor(order);
    }
  };

  return (
    <div className="dhistory__overlay" onMouseDown={handleOverlayMouseDown}>
      <div className="dhistory">
        <div className="dhistory__header">
          <h2 className="dhistory__title">
            História dlhu – {customer?.firstname} {customer?.lastname}
          </h2>
          <div className="dhistory__header-actions">
            <button 
              className="dhistory__pay-btn"
              onClick={() => {
                onClose();
                onOpenPayment();
              }}
              title="Prijať platbu"
            >
              <Banknote size={14} />
              <span>Prijať platbu</span>
            </button>
            <button className="dhistory__close" onClick={onClose} aria-label="Zavrieť">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="dhistory__body">
          {loading ? (
            <div className="dhistory__loader">
              <Loader2 className="dhistory__spinner" size={28} />
              <span>Načítavam...</span>
            </div>
          ) : ordersWithRunning.length === 0 ? (
            <p className="dhistory__empty">Žiadne objednávky nenájdené.</p>
          ) : (
            <>
              <div className="dhistory__scroll" style={{ display: viewingItemsFor ? 'none' : 'block' }}>
                <table className="dhistory__table">
                  <thead>
                    <tr>
                      <th className="dhistory__th">Dátum</th>
                      <th className="dhistory__th hide-on-mobile">Položky</th>
                      <th className="dhistory__th dhistory__th--right">Cena</th>
                      <th className="dhistory__th dhistory__th--right">Zaplatené</th>
                      <th className="dhistory__th dhistory__th--right">Pohyb (dlh)</th>
                      <th className="dhistory__th dhistory__th--right">Zostatok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordersWithRunning.map((order) => {
                      const balanceVal = parseFloat(order.balance) || 0;
                      const runningVal = order.running_balance;
                      return (
                        <React.Fragment key={order.id}>
                          <tr className="history-order-header">
                            <td colSpan={6}>
                              <div className="header-content">
                                <span className="order-number">{order.order_code}</span>
                                <span className={`dhistory__status dhistory__status--${(order.status || "").toLowerCase()}`}>
                                  {STATUS_LABELS[order.status] || order.status}
                                </span>
                              </div>
                            </td>
                          </tr>
                          <tr 
                            className="history-order-data"
                            onClick={() => handleRowClick(order)}
                            style={{ cursor: window.innerWidth < 768 && Array.isArray(order.items) && order.items.length > 0 ? 'pointer' : 'default' }}
                          >
                            <td className="dhistory__td">
                              {(() => {
                                const { dayMonth, year } = formatDateMobile(order.delivery_date);
                                return (
                                  <>
                                    <div style={{ whiteSpace: 'nowrap' }}>{dayMonth}</div>
                                    <div>{year}</div>
                                  </>
                                );
                              })()}
                            </td>
                            <td className="dhistory__td dhistory__td--items hide-on-mobile">
                              {Array.isArray(order.items) && order.items.length > 0
                                ? order.items.map((item, index) => {
                                    const mealName = item.meal_code || item.name || item.meal_name || item.title || 'Neznáme jedlo';
                                    const quantity = item.quantity || 1;
                                    return <div key={index}>{quantity}x {mealName}</div>;
                                  })
                                : <span className="dhistory__items-payment">Vyplatenie dlhu</span>}
                            </td>
                            <td className="dhistory__td dhistory__td--right">
                              {formatAmount(order.total_price)}
                            </td>
                            <td className="dhistory__td dhistory__td--right">
                              {formatAmount(order.amount_paid)}
                            </td>
                            <td className={`dhistory__td dhistory__td--right ${balanceVal < 0 ? "dhistory__td--debt" : balanceVal > 0 ? "dhistory__td--credit" : ""}`}>
                              {balanceVal > 0 ? "+" : ""}{formatAmount(balanceVal)}
                            </td>
                            <td className={`dhistory__td dhistory__td--right dhistory__td--balance dhistory__td--debt`}>
                              {formatAmount(runningVal)}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {viewingItemsFor && (
                <div className="dhistory__items-detail">
                  <button 
                    className="dhistory__back-btn" 
                    onClick={() => setViewingItemsFor(null)}
                  >
                    ← Späť na históriu
                  </button>
                  <div className="dhistory__items-list">
                    <h3 className="dhistory__items-list-title">Položky objednávky {viewingItemsFor.order_code}</h3>
                    {Array.isArray(viewingItemsFor.items) && viewingItemsFor.items.length > 0 ? (
                      <ul className="dhistory__items-list-ul">
                        {viewingItemsFor.items.map((item, index) => {
                          const mealName = item.meal_code || item.name || item.meal_name || item.title || 'Neznáme jedlo';
                          const quantity = item.quantity || 1;
                          return (
                            <li key={index} className="dhistory__items-list-li">
                              {quantity}x {mealName}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="dhistory__items-list-empty">Vyplatenie dlhu</p>
                    )}
                  </div>
                </div>
              )}

              <div className="sticky-debt-footer">
                <span>Aktuálny celkový dlh</span>
                <span className={parseFloat(customer?.actual_saldo) > 0 ? "sticky-debt-footer__amount--debt" : ""}>
                  {formatAmount(customer?.actual_saldo ?? 0)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DebtHistoryModal;
